import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format, lastDayOfMonth } from 'date-fns';
import { safeFormat } from '@/lib/safeDate';

export interface BudgetVsActualItem {
  accountCode: string;
  accountName: string;
  accountType: string;
  accountCategory: string | null;
  budgeted: number;
  actual: number;
  variance: number;
  variancePercent: number;
  status: 'ok' | 'warning' | 'critical';
  currency: string;
}

export interface BudgetVsActualSummary {
  totalBudgeted: number;
  totalActual: number;
  totalVariance: number;
  totalVariancePercent: number;
  items: BudgetVsActualItem[];
}

export function useBudgetVsActual(year: number, month: number, country?: string) {
  return useQuery({
    queryKey: ['budget-vs-actual', year, month, country],
    queryFn: async () => {
      // 1. Buscar orçamento do mês
      let budgetQuery = supabase
        .from('budget_entries')
        .select(`
          account_code,
          planned_amount,
          currency,
          account:chart_of_accounts(code, name, type, category)
        `)
        .eq('year', year)
        .eq('month', month);

      if (country && country !== 'all') {
        budgetQuery = budgetQuery.eq('country', country);
      }

      const { data: budgetData, error: budgetError } = await budgetQuery;
      if (budgetError) throw budgetError;

      // 2. Buscar despesas reais do mês (pagas)
      const startDate = safeFormat(new Date(year, month - 1, 1), 'yyyy-MM-dd');
      const endDate = safeFormat(lastDayOfMonth(new Date(year, month - 1, 1)), 'yyyy-MM-dd');

      let expensesQuery = supabase
        .from('expenses')
        .select('account_code, amount, currency')
        .eq('status', 'paid')
        .gte('paid_at', startDate)
        .lte('paid_at', endDate);

      if (country && country !== 'all') {
        expensesQuery = expensesQuery.eq('business_unit', country === 'usa' ? 'us' : 'br');
      }

      const { data: expensesData, error: expensesError } = await expensesQuery;
      if (expensesError) throw expensesError;

      // 3. Agrupar por conta
      const byAccount: Record<string, {
        budgeted: number;
        actual: number;
        accountName: string;
        accountType: string;
        accountCategory: string | null;
        currency: string;
      }> = {};

      // Adicionar dados do orçamento
      budgetData?.forEach(b => {
        const code = b.account_code || 'unknown';
        const account = b.account as { code: string; name: string; type: string; category: string | null } | null;
        
        if (!byAccount[code]) {
          byAccount[code] = {
            budgeted: 0,
            actual: 0,
            accountName: account?.name || code,
            accountType: account?.type || 'expense',
            accountCategory: account?.category || null,
            currency: b.currency || 'USD',
          };
        }
        byAccount[code].budgeted += Number(b.planned_amount) || 0;
      });

      // Adicionar dados reais
      expensesData?.forEach(e => {
        const code = e.account_code || 'unknown';
        if (!byAccount[code]) {
          byAccount[code] = {
            budgeted: 0,
            actual: 0,
            accountName: code,
            accountType: 'expense',
            accountCategory: null,
            currency: e.currency || 'USD',
          };
        }
        byAccount[code].actual += Number(e.amount) || 0;
      });

      // 4. Calcular variância
      const items: BudgetVsActualItem[] = Object.entries(byAccount).map(([code, values]) => {
        const variance = values.actual - values.budgeted;
        const variancePercent = values.budgeted > 0
          ? (variance / values.budgeted) * 100
          : values.actual > 0 ? 100 : 0;

        let status: 'ok' | 'warning' | 'critical' = 'ok';
        if (values.budgeted > 0) {
          const absVariance = Math.abs(variancePercent);
          if (absVariance > 15) {
            status = 'critical';
          } else if (absVariance > 5) {
            status = 'warning';
          }
        }

        return {
          accountCode: code,
          accountName: values.accountName,
          accountType: values.accountType,
          accountCategory: values.accountCategory,
          budgeted: values.budgeted,
          actual: values.actual,
          variance,
          variancePercent,
          status,
          currency: values.currency,
        };
      });

      // Ordenar por código de conta
      items.sort((a, b) => a.accountCode.localeCompare(b.accountCode));

      // Calcular totais
      const totalBudgeted = items.reduce((sum, i) => sum + i.budgeted, 0);
      const totalActual = items.reduce((sum, i) => sum + i.actual, 0);
      const totalVariance = totalActual - totalBudgeted;
      const totalVariancePercent = totalBudgeted > 0
        ? (totalVariance / totalBudgeted) * 100
        : 0;

      return {
        totalBudgeted,
        totalActual,
        totalVariance,
        totalVariancePercent,
        items,
      } as BudgetVsActualSummary;
    },
  });
}

// Tendência de despesas para uma conta específica
export function useExpenseTrend(accountCode: string, months: number = 12) {
  return useQuery({
    queryKey: ['expense-trend', accountCode, months],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('expense_history')
        .select('*')
        .eq('account_code', accountCode)
        .order('year', { ascending: true })
        .order('month', { ascending: true })
        .limit(months);

      if (error) throw error;
      return data;
    },
    enabled: !!accountCode,
  });
}

// Médias de despesas variáveis
export function useExpenseAverages(recurringExpenseId?: string, accountCode?: string) {
  return useQuery({
    queryKey: ['expense-averages', recurringExpenseId, accountCode],
    queryFn: async () => {
      let query = supabase
        .from('expense_history')
        .select('amount, currency')
        .gte('created_at', new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString());

      if (recurringExpenseId) {
        query = query.eq('recurring_expense_id', recurringExpenseId);
      }
      if (accountCode) {
        query = query.eq('account_code', accountCode);
      }

      const { data, error } = await query;
      if (error) throw error;

      if (!data || data.length === 0) {
        return null;
      }

      const amounts = data.map(d => Number(d.amount));
      const sum = amounts.reduce((a, b) => a + b, 0);
      const avg = sum / amounts.length;
      const min = Math.min(...amounts);
      const max = Math.max(...amounts);

      // Mediana
      const sorted = [...amounts].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      const median = sorted.length % 2 !== 0
        ? sorted[mid]
        : (sorted[mid - 1] + sorted[mid]) / 2;

      return {
        count: amounts.length,
        average: avg,
        min,
        max,
        median,
        currency: data[0].currency || 'USD',
      };
    },
    enabled: !!(recurringExpenseId || accountCode),
  });
}
